import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import caseService from '../services/caseService';
import authService from '../services/authService';
import api from '../utils/api';
import FilterModal from '../components/common/FilterModal';
import ProfileAvatar from '../components/common/ProfileAvatar';
import '../styles/CasesPage.css';
import '../styles/Casespage pdf modal.css';

export default function CasesPage() {
  const [cases,       setCases]       = useState([]);
  const [filtered,    setFiltered]    = useState([]);
  const [placeMap,    setPlaceMap]    = useState({});
  const [vanMap,      setVanMap]      = useState({});
  const [userRole,    setUserRole]    = useState('');
  const [page,        setPage]        = useState(1);
  const [perPage,     setPerPage]     = useState(10);
  const [searchParams]                = useSearchParams();
  const navigate                      = useNavigate();
  const location                      = useLocation();

  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState({
    regNo: '', patientName: '', startDate: '', endDate: '', place: '', reportStatus: ''
  });
  const [searchQ, setSearchQ] = useState(searchParams.get('q') || '');

  // loadingCombined tracks which case_id is currently fetching (shows spinner)
  const [loadingCombined, setLoadingCombined] = useState(null);

  // ── Inline PDF viewer state ──────────────────────────────────────────
  const [pdfModal, setPdfModal] = useState({ open: false, url: null, title: '' });

  const closePdfModal = useCallback(() => {
    if (pdfModal.url) {
      URL.revokeObjectURL(pdfModal.url);
    }
    setPdfModal({ open: false, url: null, title: '' });
  }, [pdfModal.url]);

  // Close modal on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') closePdfModal(); };
    if (pdfModal.open) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [pdfModal.open, closePdfModal]);

  const navCls = (path) =>
    `cdp__nav-btn${location.pathname === path ? ' cdp__nav-btn--active' : ''}`;

  /* ── Load cases + build place & van map ── */
  useEffect(() => {
    caseService.getAll()
      .then(d => { setCases(d); setFiltered(d); })
      .catch(() => {});

    authService.getMe()
      .then(me => { if (me?.role) setUserRole(me.role); })
      .catch(() => {});

    authService.getUsers()
      .then(adminsData => {
        const pMap = {};
        const vMap = {};
        adminsData.forEach(u => {
          if (u.username) {
            pMap[u.username] = u.place || '';
            vMap[u.username] = u.van_no || '';
          }
        });
        setPlaceMap(pMap);
        setVanMap(vMap);
      })
      .catch(() => {
        authService.getMe()
          .then(me => {
            if (me?.username) {
              setPlaceMap({ [me.username]: me.place || '' });
              setVanMap({ [me.username]: me.van_no || '' });
            }
          })
          .catch(() => {});
      });
  }, []);

  const resolvePlace = useCallback((c, map) => {
    return c.place || map[c.uploaded_by_name] || '';
  }, []);

  const applyFilters = useCallback((allCases, q, fd, map) => {
    let d = [...allCases];
    if (q && q.trim()) {
      const lq = q.toLowerCase();
      d = d.filter(c =>
        c.patient_name?.toLowerCase().includes(lq) ||
        String(c.case_id).includes(lq) ||
        c.patient_id_display?.toLowerCase().includes(lq)
      );
    }
    if (fd.regNo && fd.regNo.trim())
      d = d.filter(c => String(c.patient_id_display ?? '').toLowerCase().includes(fd.regNo.toLowerCase()));
    if (fd.patientName && fd.patientName.trim())
      d = d.filter(c => String(c.patient_name ?? '').toLowerCase().includes(fd.patientName.toLowerCase()));
    if (fd.place && fd.place.trim())
      d = d.filter(c => String(resolvePlace(c, map)).toLowerCase().includes(fd.place.toLowerCase()));
    if (fd.reportStatus && fd.reportStatus.trim())
      d = d.filter(c => [c.birads_score, c.left_birads_score, c.right_birads_score]
        .some(v => String(v) === String(fd.reportStatus)));
    if (fd.startDate && fd.startDate.trim())
      d = d.filter(c => { const sd = c.scan_date ? String(c.scan_date).slice(0, 10) : ''; return sd !== '' && sd >= fd.startDate; });
    if (fd.endDate && fd.endDate.trim())
      d = d.filter(c => { const sd = c.scan_date ? String(c.scan_date).slice(0, 10) : ''; return sd !== '' && sd <= fd.endDate; });
    return d;
  }, [resolvePlace]);

  useEffect(() => {
    const result = applyFilters(cases, searchQ, filterData, placeMap);
    setFiltered(result);
    setPage(1);
  }, [searchQ, filterData, cases, placeMap, applyFilters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const slice      = filtered.slice((page - 1) * perPage, page * perPage);

  const getReportStatus = (score, leftScore, rightScore) => {
    const hasLeft  = leftScore  != null && leftScore  !== '';
    const hasRight = rightScore != null && rightScore !== '';

    if (hasLeft || hasRight) {
      const leftText  = hasLeft  ? `L: BI-RADS-${leftScore}`  : null;
      const rightText = hasRight ? `R: BI-RADS-${rightScore}` : null;
      const combined  = [leftText, rightText].filter(Boolean).join(' / ');
      const highest   = Number(
        hasLeft && hasRight
          ? Math.max(leftScore, rightScore)
          : hasLeft ? leftScore : rightScore
      );
      return {
        text: combined || 'Pending',
        kind: Number.isFinite(highest) ? `birads-${highest}` : 'pending',
      };
    }

    if (score == null || score === '') return { text: 'Pending', kind: 'pending' };
    const n = Number(score);
    if (!Number.isFinite(n)) return { text: 'Pending', kind: 'pending' };
    return { text: `BI-RADS-${n}`, kind: `birads-${n}` };
  };

  const isSuperadmin = userRole === 'superadmin';

  const exportXLS = () => {
    const rows = [
      isSuperadmin
        ? ['Reg No','Name','Date','Created By','Van No','Place','Report Status']
        : ['Reg No','Name','Date','Van No','Place','Report Status'],
      ...filtered.map(c => {
        const s = getReportStatus(c.birads_score, c.left_birads_score, c.right_birads_score);
        return isSuperadmin
          ? [c.patient_id_display, c.patient_name, c.scan_date, c.uploaded_by_name || 'N/A', vanMap[c.uploaded_by_name] || c.van_no || 'N/A', resolvePlace(c, placeMap) || 'N/A', s.text]
          : [c.patient_id_display, c.patient_name, c.scan_date, vanMap[c.uploaded_by_name] || c.van_no || 'N/A', resolvePlace(c, placeMap) || 'N/A', s.text];
      }),
    ];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: 'cases.csv' }).click();
    URL.revokeObjectURL(url);
  };

  const buildPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const s = new Set([1, 2, 3, 4, 5, totalPages]);
    if (page > 5 && page < totalPages) { s.add(page - 1); s.add(page); s.add(page + 1); }
    return Array.from(s).sort((a, b) => a - b);
  };
  const pageNums = buildPages();

  const activeFilterCount = Object.values(filterData).filter(v => v && v.trim()).length;

  const superadminGrid = '9% 18% 11% 12% 9% 11% 13% 11% 6%';
  const adminGrid      = '10% 20% 13% 10% 13% 14% 12% 8%';
  const gridCols       = isSuperadmin ? superadminGrid : adminGrid;

  // ── Open combined PDF INLINE (no new tab) ────────────────────────────
  const openCombinedReport = useCallback(async (caseId, patientName) => {
    if (loadingCombined === caseId) return;
    setLoadingCombined(caseId);
    try {
      const response = await api.get(`/reports/${caseId}/combined/`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      setPdfModal({
        open: true,
        url: blobUrl,
        title: `Combined Report — ${patientName || caseId}`,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        alert('Combined report not found. The case may still be processing — please try again in a moment.');
      } else if (status === 401 || status === 403) {
        alert('Session expired. Please log in again.');
      } else {
        alert(`Failed to open combined report (${status || 'network error'}). Please try again.`);
      }
      console.error('openCombinedReport error:', err);
    } finally {
      setLoadingCombined(null);
    }
  }, [loadingCombined]);

  return (
    <div className="cdp__root">

      {/* ══ NAVBAR ══ */}
      <nav className="cdp__navbar">
        <span className="cdp__nav-title">
          {isSuperadmin ? 'Super Admin' : 'Admin'}
        </span>

        <div className="cdp__nav-right">
          {isSuperadmin && (
            <button className={navCls('/admin')} onClick={() => navigate('/admin')}>
              View Admins
            </button>
          )}
          <button className={navCls('/patients')} onClick={() => navigate('/patients')}>
            Start Analysis
          </button>
          <button className={navCls('/cases')} onClick={() => navigate('/cases')}>
            Dashboard
          </button>
          <ProfileAvatar />
        </div>
      </nav>

      {/* ══ SUB-BAR ══ */}
      <div className="cdp__subbar">
        <span className="cdp__section-title">
          {isSuperadmin ? 'Admin Dashboard' : 'Patient Details'}
        </span>
        <div className="cdp__subbar-right">
          <div className="cdp__search-wrap">
            <input
              type="text"
              className="cdp__search-input"
              placeholder="Search"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
            />
            <i className="fa-solid fa-magnifying-glass cdp__search-icon" />
          </div>
          <button
            className="cdp__btn-filter-icon"
            onClick={() => setShowFilter(true)}
            title="Filter"
            style={{ position: 'relative' }}
          >
            <i className="fa-solid fa-sliders" />
            {activeFilterCount > 0 && (
              <span style={{
                position: 'absolute', top: -6, right: -6,
                background: '#e91e8c', color: '#fff',
                borderRadius: '50%', width: 18, height: 18,
                fontSize: 11, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontWeight: 700,
              }}>
                {activeFilterCount}
              </span>
            )}
          </button>
          <button className="cdp__btn-xls" onClick={exportXLS} title="Export to Excel">
            <span className="cdp__xls-icon">
              <i className="fa-solid fa-file-excel" />
            </span>
          </button>
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div className="cdp__body">
        <div className="cdp__card">

          {/* ── Table header ── */}
          <div className="cdp__thead" style={{ gridTemplateColumns: gridCols }}>
            <div className="cdp__col cdp__col--reg">Reg No</div>
            <div className="cdp__col cdp__col--name">NAME</div>
            <div className="cdp__col cdp__col--date">DATE</div>
            {isSuperadmin && <div className="cdp__col cdp__col--created-by">Created By</div>}
            <div className="cdp__col cdp__col--van">Van No</div>
            <div className="cdp__col cdp__col--place">Place</div>
            <div className="cdp__col cdp__col--status">Report Status</div>
            <div className="cdp__col cdp__col--combined">Combined Report</div>
            <div className="cdp__col cdp__col--view">View</div>
          </div>

          {/* ── Rows ── */}
          <div className="cdp__rows-area">
            {slice.length === 0 ? (
              <div className="cdp__empty">
                <i className="fa-solid fa-folder-open fa-2x mb-2 d-block" />
                No records found
              </div>
            ) : (
              slice.map((c) => {
                const status  = getReportStatus(c.birads_score, c.left_birads_score, c.right_birads_score);
                const place   = resolvePlace(c, placeMap);
                const vanNo   = vanMap[c.uploaded_by_name] || c.van_no || 'N/A';
                const isBusy  = loadingCombined === c.case_id;
                return (
                  <div key={c.case_id} className="cdp__row" style={{ gridTemplateColumns: gridCols }}>
                    <div className="cdp__col cdp__col--reg cdp__cell-reg">{c.patient_id_display}</div>
                    <div className="cdp__col cdp__col--name">{c.patient_name}</div>
                    <div className="cdp__col cdp__col--date">{c.scan_date}</div>
                    {isSuperadmin && (
                      <div className="cdp__col cdp__col--created-by">{c.uploaded_by_name || 'N/A'}</div>
                    )}
                    <div className="cdp__col cdp__col--van">{vanNo}</div>
                    <div className="cdp__col cdp__col--place cdp__cell-place">{place || 'N/A'}</div>
                    <div className="cdp__col cdp__col--status">
                      <span className={`cdp__status cdp__status--${status.kind}`}>{status.text}</span>
                    </div>

                    {/* ── Combined Report (navigate to page) ── */}
                    <div className="cdp__col cdp__col--combined">
                      <button
                        className="cdp__btn-eye"
                        title="View Combined Report (Doctor + AI)"
                        onClick={() => navigate(`/cases/${c.case_id}/combined`)}
                      >
                        <i className="fa-regular fa-eye" />
                      </button>
                    </div>

                    {/* ── AI Report ── */}
                    <div className="cdp__col cdp__col--view">
                      <button
                        className="cdp__btn-eye"
                        onClick={() => navigate(`/cases/${c.case_id}/report`)}
                        title="View AI Report"
                      >
                        <i className="fa-regular fa-eye" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* ── Paginator ── */}
          <div className="cdp__pager-row">
            <div className="cdp__pager-left">
              <div className="cdp__pager-controls">
                <span className="cdp__pager-label">Show</span>
                <select
                  className="cdp__per-page-select"
                  value={perPage}
                  onChange={e => {
                    const newPerPage = Number(e.target.value);
                    const currentRowIndex = (page - 1) * perPage;
                    const newPage = Math.floor(currentRowIndex / newPerPage) + 1;
                    setPerPage(newPerPage);
                    setPage(newPage);
                  }}
                >
                  {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="cdp__pager-label">Row</span>
              </div>

              <div className="cdp__pager-nav">
                <button
                  className="cdp__pager-btn cdp__pager-arrow"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  aria-label="Previous page"
                  title="Previous"
                >
                  <i className="fa-solid fa-chevron-left" />
                </button>

                {pageNums.map((p, i, arr) => (
                  <React.Fragment key={p}>
                    {i > 0 && arr[i - 1] !== p - 1 && (
                      <button className="cdp__pager-btn cdp__pager-ellipsis" disabled>…</button>
                    )}
                    <button
                      className={`cdp__pager-btn${p === page ? ' cdp__pager-active' : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}

                <button
                  className="cdp__pager-btn cdp__pager-arrow"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                  aria-label="Next page"
                  title="Next"
                >
                  <i className="fa-solid fa-chevron-right" />
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ══ INLINE PDF VIEWER MODAL ══ */}
      {pdfModal.open && (
        <div className="cdp__pdf-overlay" onClick={closePdfModal}>
          <div
            className="cdp__pdf-modal"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="cdp__pdf-modal-header">
              <span className="cdp__pdf-modal-title">{pdfModal.title}</span>
              <div className="cdp__pdf-modal-actions">
                <a
                  href={pdfModal.url}
                  download="combined_report.pdf"
                  className="cdp__pdf-download-btn"
                  title="Download PDF"
                >
                  <i className="fa-solid fa-download" />
                </a>
                <button
                  className="cdp__pdf-close-btn"
                  onClick={closePdfModal}
                  title="Close"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            </div>

            {/* PDF iframe */}
            <iframe
              src={pdfModal.url}
              className="cdp__pdf-iframe"
              title="Combined Report"
            />
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilter && (
        <FilterModal
          onClose={() => setShowFilter(false)}
          onApply={(data) => { setFilterData({ ...data }); setShowFilter(false); }}
          onClear={() => { setFilterData({ regNo:'', patientName:'', startDate:'', endDate:'', place:'', reportStatus: '' }); setShowFilter(false); }}
          initialData={filterData}
        />
      )}
    </div>
  );
}